import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Ban, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Dialog } from '../components/ui/Dialog';
import { Input, Label } from '../components/ui/Input';
import { Table, THead, TBody, TR, TH, TD } from '../components/ui/Table';
import { PageLoader } from '../components/ui/Spinner';
import { EmptyState } from '../components/ui/EmptyState';
import { apiFetch } from '../lib/apiFetch';
import { formatDateTime } from '../lib/format';
import { useT } from '../i18n';

interface KickItem {
  id: number;
  kicked_at: string;
  user_id: number;
  user_full_name: string | null;
  user_username: string | null;
  channel_id: number;
  channel_title: string;
  reason: string;
  is_restored: boolean;
  restored_at: string | null;
}

export function Kicks() {
  const t = useT();
  const [restoring, setRestoring] = useState<KickItem | null>(null);
  const [days, setDays] = useState('30');
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['kicks'],
    queryFn: () => apiFetch<{ items: KickItem[] }>('/api/admin/kicks', { query: { limit: 100 } }),
  });

  const restore = useMutation({
    mutationFn: ({ id, days }: { id: number; days: number }) =>
      apiFetch(`/api/admin/kicks/${id}/restore`, { method: 'POST', body: { days } }),
    onSuccess: () => {
      setRestoring(null);
      qc.invalidateQueries({ queryKey: ['kicks'] });
    },
  });

  return (
    <div className="space-y-4">
      {list.isLoading ? (
        <PageLoader />
      ) : !list.data || list.data.items.length === 0 ? (
        <EmptyState icon={<Ban className="h-10 w-10" />} title={t('kicks.emptyTitle')} />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH>{t('common.time')}</TH>
                  <TH>{t('kicks.student')}</TH>
                  <TH>{t('kicks.channel')}</TH>
                  <TH>{t('common.reason')}</TH>
                  <TH>{t('common.status')}</TH>
                  <TH className="text-right">{t('common.action')}</TH>
                </TR>
              </THead>
              <TBody>
                {list.data.items.map((k) => (
                  <TR key={k.id}>
                    <TD className="text-sm text-slate-600">{formatDateTime(k.kicked_at)}</TD>
                    <TD>
                      <div className="font-medium">{k.user_full_name ?? `#${k.user_id}`}</div>
                      {k.user_username && <div className="text-xs text-slate-500">@{k.user_username}</div>}
                    </TD>
                    <TD>{k.channel_title}</TD>
                    <TD className="text-sm">{k.reason}</TD>
                    <TD>
                      {k.is_restored ? (
                        <Badge variant="success">{t('kicks.restored')}</Badge>
                      ) : (
                        <Badge variant="destructive">{t('kicks.kicked')}</Badge>
                      )}
                    </TD>
                    <TD className="text-right">
                      {!k.is_restored && (
                        <Button size="sm" variant="outline" onClick={() => setRestoring(k)}>
                          <RotateCcw className="h-4 w-4" />
                          {t('kicks.restore')}
                        </Button>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={!!restoring}
        onClose={() => setRestoring(null)}
        title={t('kicks.restoreTitle')}
        description={t('kicks.restoreDescription')}
      >
        <div className="space-y-3">
          <Label htmlFor="kdays">{t('kicks.extendDays')}</Label>
          <Input id="kdays" type="number" min="1" max="365" value={days} onChange={(e) => setDays(e.target.value)} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRestoring(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={restore.isPending}
              onClick={() => restoring && restore.mutate({ id: restoring.id, days: Number(days) || 30 })}
            >
              {t('kicks.restore')}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
